import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import Bubble from 'components/bubble';
import Button from 'components/buttons/button';
import CTA from 'components/Campaign/CTA';
import { Drive } from 'components/cards';
import { Petition } from 'components/cards/petition';
import Counter from 'components/counter';
import Layout from 'components/layout';
import ShareBlock from 'components/Sharing';
import Tabs from 'components/tabs';
import Anchor from 'components/typography/anchor';
import NoUserContent from 'components/user/NoUserContent';
import userShareImg from 'images/shares/trending.png';
import { getPetitionsForUserCode } from 'lib/api/petitions';
import {
  getTrendingPetitionsV2,
  getUser,
  getUserContent,
} from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import {
  fieldsListView,
  PETITION_SIGNING_STATES,
  trimPetitions,
} from 'lib/petitions';
import { driveToUserContentSlim } from 'lib/transformers';
import PropTypes from 'prop-types';
import { useState } from 'react';
import config, { DATA_TYPES } from 'siteconfig';
import { getOpenGraphUrl } from 'utilities/paths';
import { CampaignTypeDriveList, CampaignTypeList } from 'utilities/propTypes';

const BASE_PATH = 'go';
// Active organizers can have hundreds of campaigns. Rendering them all made
// the page several thousand elements long, so show them a page at a time.
const CAMPAIGNS_PER_PAGE = 25;

// `?page=N` is a real, crawlable page of open petitions; "Show more" links to
// the next one so search engines can reach every petition. Page 1 has no
// param, so there's one URL for it.
function userPagePath(usercode, page = 1) {
  const url = new URL(`/${BASE_PATH}/${usercode}`, config.siteUrl);
  if (page > 1) {
    url.searchParams.set('page', String(page));
  }
  return `${url.pathname}${url.search}`;
}

function parsePage(value) {
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 ? page : null;
}

export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async ({ params, query }) => {
    const parentCtx = otelApi.context.active();
    const parentSpan = otelApi.trace
      .getTracer('Gin Rickey')
      .startSpan('go/[usercode]', undefined, parentCtx);

    parentSpan.setAttributes({
      [ATTR_HTTP_ROUTE]: `/go/${params.usercode}`,
    });

    // For consistency reasons we jsut do this here.  Display of usernames is always lowercase (except the keyword bubbles)
    // and the cassini endpoint will normalize everything uppercase.
    const usercode = params.usercode.toLowerCase();

    // If the case was wrong on the sharecode, redirect to uppercased version
    // This should help SEO and reduce people sharing the wrong one
    if (usercode !== params.usercode) {
      return {
        redirect: {
          // Keep the page they asked for
          destination: userPagePath(usercode, parsePage(query.page) ?? 1),
          permanent: true,
        },
      };
    }

    const userPetitionsPromise = getPetitionsForUserCode({ usercode });
    const themisUserPromise = getUser({ code: usercode.toUpperCase() });

    const userResults = await Promise.all([
      userPetitionsPromise,
      themisUserPromise,
    ]);

    const user = userResults[0];
    const themisUser = userResults[1];

    // Failing to find the user code on petitions lookup implies the code is not found, so bail out
    if (user.notFound) {
      parentSpan.setStatus({
        code: SpanStatusCode.OK,
      });
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });
      parentSpan.end();
      return {
        notFound: true,
      };
    }

    // Not great but E2E CI struggled to get past here with env vars not loading
    const themisUserContent = themisUser
      ? await getUserContent({
          userId: themisUser.id,
        })
      : {};
    const drivesFull = themisUserContent?.items || [];
    const trendingPetitions = await getTrendingPetitionsV2({
      range: 'SEVEN_DAY',
      limit: 12,
    });

    const open = [];
    const closed = [];
    let signCount = 0;

    const openDrives = [];
    const closedDrives = [];
    // Get slimmed drives and add their counts
    const drives = drivesFull.map((d) =>
      driveToUserContentSlim({ apiDrive: d })
    );
    drives.forEach((d) => {
      signCount += d.signCount;

      if (d.signStatus === PETITION_SIGNING_STATES.OPEN) {
        openDrives.push(d);
      } else {
        closedDrives.push(d);
      }
    });

    // Process each to add trending data and split into open/closed
    user.petitions.forEach((p) => {
      const sharecode = p.user_share_code;
      const isTrending = trendingPetitions.filter(
        (p) => p.petition_code === sharecode
      );

      // Move this to secondary level for if we ever use different time periods
      // This also happens in packages/site/src/lib/api/petitions/index.js
      p.trending = {
        sign_count_period: isTrending[0]?.trending?.sign_count_period || null,
      };

      // Add up all the total signatures they've gotten
      signCount = signCount + p.sign_count || 0;

      const is_closed = p.sign_status === PETITION_SIGNING_STATES.CLOSED;

      if (is_closed) {
        closed.push(p);
      } else {
        open.push(p);
      }
    });

    const slimOpen = trimPetitions({
      petitions: open,
      fieldsToKeep: fieldsListView,
    });
    const slimClosed = trimPetitions({
      petitions: closed,
      fieldsToKeep: fieldsListView,
    });

    // Out-of-range or malformed pages are 404s rather than empty or duplicate
    // copies of page 1
    const pageCount = Math.max(1, Math.ceil(open.length / CAMPAIGNS_PER_PAGE));
    const page = query.page === undefined ? 1 : parsePage(query.page);
    if (page === null || page > pageCount) {
      parentSpan.end();
      return { notFound: true };
    }

    parentSpan.end();
    return {
      props: {
        usercode,
        page,
        signCount,
        petitions: {
          open: slimOpen,
          closed: slimClosed,
        },
        drives: {
          open: openDrives,
          closed: closedDrives,
        },
        user: {
          followers: { count: themisUser?.follow?.followersCount || 0 },
          following: { count: themisUser?.follow?.followingCount || 0 },
        },
      },
    };
  }
);

const User = ({ usercode, page, petitions, signCount, drives, user }) => {
  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/user/user', {
    name: '', // we don't currently support this here
    usercode,
    totalSigns: signCount,
  });

  const hasNoContent =
    petitions.open.length === 0 &&
    petitions.closed.length === 0 &&
    drives.open.length === 0 &&
    drives.closed.length === 0;

  return (
    <Layout
      title={
        page > 1
          ? `${usercode} on Resistbot (page ${page})`
          : `${usercode} on Resistbot`
      }
      description={`Join the campaigns ${usercode} is running on Resistbot!`}
      image={userShareImg}
      dynamicImage={dynamicOpenGraphUrlHref}
      imageAltText={`Text Follow ${usercode} to 50409`}
      canonicalPath={userPagePath(usercode.toLowerCase(), page)}
    >
      <div className="mb-10 flex flex-wrap items-baseline justify-center gap-4 sm:justify-between">
        <h1>{usercode}</h1>
        <section className="flex flex-nowrap gap-4">
          <Counter count={user.followers.count} text="Followers" />
          <Counter count={signCount} text="Signers" />
        </section>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row">
        <div className="grow">
          {hasNoContent ? (
            <NoUserContent />
          ) : (
            <>
              <CampaignTypeSection
                open={drives.open}
                closed={drives.closed}
                usercode={usercode}
                type={DATA_TYPES.DRIVE}
              />
              <CampaignTypeSection
                open={petitions.open}
                closed={petitions.closed}
                usercode={usercode}
                type={DATA_TYPES.PETITION}
                page={page}
              />
            </>
          )}
        </div>
        <div className="basis-80">
          <CTA
            copy={`Follow ${usercode}`}
            shareCode={usercode}
            type={DATA_TYPES.USER}
          />
          <p className="mb-4">
            Text <Bubble>Follow {usercode}</Bubble> to get a text when each
            campaign starts!
          </p>
          <p className="mb-4">
            Use the <Bubble>CLAIM</Bubble> keyword to get your own page,
            keyword, and to build a following on Resistbot. Followers{' '}
            <Anchor
              className="anchor-subtle"
              href="/news/2020/01/14/introducing-petitions"
            >
              receive texts for every campaign
            </Anchor>{' '}
            you create.
          </p>
          <ShareBlock
            // Share the organizer's page itself, not whichever page this is
            pathname={userPagePath(usercode)}
            text={`Text FOLLOW ${usercode} to 50409`}
          />
        </div>
      </div>
    </Layout>
  );
};

const CampaignTypeSection = ({ open, closed, usercode, type, page }) => {
  let headingText = 'UNKNOWN TYPE';
  let subText = 'UNKNOWN ACTION';
  switch (type) {
    case DATA_TYPES.PETITION:
      headingText = 'Petitions';
      subText =
        'To change laws and public policy at the federal, state, or local level.';
      break;
    case DATA_TYPES.DRIVE:
      headingText = 'Vote Drives';
      subText = 'To elect candidates up and down ballot.';
      break;
    default:
      break;
  }
  if (open.length > 0 || closed.length > 0) {
    return (
      <>
        <h2>{headingText}</h2>
        <p className="mb-4 text-sm">{subText}</p>
        <CampaignGroup
          closed={closed}
          open={open}
          usercode={usercode}
          type={type}
          page={page}
        />
      </>
    );
  }
  return null;
};

// Shows one page of campaigns, starting at `firstPage`, plus a button for the
// next. With `pageHref` the button is a link to that page for crawlers; with
// JavaScript it adds the next page in place instead.
const PagedList = ({ items, renderItem, firstPage = 1, pageHref }) => {
  const start = (firstPage - 1) * CAMPAIGNS_PER_PAGE;
  const [end, setEnd] = useState(start + CAMPAIGNS_PER_PAGE);
  const remaining = items.length - end;
  const nextPage = end / CAMPAIGNS_PER_PAGE + 1;

  return (
    <>
      {start > 0 && pageHref && (
        <Anchor href={pageHref(1)} className="anchor-subtle mb-4 block text-sm">
          Back to the first page
        </Anchor>
      )}
      {items
        .slice(start, end)
        .map((item, offset) => renderItem(item, start + offset))}
      {remaining > 0 && (
        <Button
          theme="light"
          href={pageHref?.(nextPage)}
          onClick={() => setEnd(end + CAMPAIGNS_PER_PAGE)}
          className="mt-1 w-full"
          preventDefault
        >
          Show {Math.min(remaining, CAMPAIGNS_PER_PAGE)} more
        </Button>
      )}
    </>
  );
};

PagedList.propTypes = {
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  firstPage: PropTypes.number,
  pageHref: PropTypes.func,
};

const CampaignGroup = ({ open, closed, usercode, type, page = 1 }) => {
  let keyPath;
  let humanType;
  switch (type) {
    case DATA_TYPES.PETITION:
      keyPath = 'petition_code';
      humanType = 'petition';
      break;
    case DATA_TYPES.DRIVE:
      keyPath = 'shareCode';
      humanType = 'drive';
      break;
    default:
      break;
  }
  const renderCampaign = (campaign, idx) =>
    type === DATA_TYPES.DRIVE ? (
      <Drive key={campaign[keyPath]} drive={campaign} />
    ) : (
      <Petition key={campaign[keyPath]} petition={campaign} idx={idx} />
    );

  // Only open petitions get crawlable pages; the other lists are short
  const isPagedByUrl = type === DATA_TYPES.PETITION;
  const openGroup = (
    <PagedList
      items={open}
      renderItem={renderCampaign}
      firstPage={isPagedByUrl ? page : 1}
      pageHref={
        isPagedByUrl ? (pageNumber) => userPagePath(usercode, pageNumber) : null
      }
    />
  );
  const closedGroup = <PagedList items={closed} renderItem={renderCampaign} />;

  const hasOpen = open.length > 0;
  const hasClosed = closed.length > 0;
  const hasBoth = hasOpen && hasClosed;
  const hasNone = !hasOpen && !hasClosed;

  const openTabText = hasOpen ? `Open (${open.length})` : `Open`;
  const closedTabText = hasClosed ? `Closed (${closed.length})` : `Closed`;

  // None of each
  if (hasNone) {
    return (
      <div className="card-flat p-10">
        Text <Bubble>Follow {usercode}</Bubble> to 50409 to receive a text when
        they launch their first campaign. Or see top{' '}
        <Anchor href="/petitions">petitions</Anchor> or{' '}
        <Anchor href="/drives">vote drives</Anchor> organized by other members.
      </div>
    );
  }
  // Some of both
  if (hasBoth) {
    return (
      <Tabs
        descriptor={`${humanType}`}
        searchDisabled={true}
        sections={[
          {
            contents: openGroup,
            title: openTabText,
          },
          {
            contents: closedGroup,
            title: closedTabText,
          },
        ]}
      ></Tabs>
    );
  }
  // Only open
  if (hasOpen && !hasClosed) {
    return openGroup;
  }
  // Only closed
  if (!hasOpen && hasClosed) {
    return (
      <Tabs
        descriptor={`${humanType}`}
        searchDisabled={true}
        sections={[
          {
            contents: openGroup,
            title: openTabText,
          },
          {
            contents: closedGroup,
            title: closedTabText,
          },
        ]}
      ></Tabs>
    );
  }
};

User.propTypes = {
  usercode: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  petitions: PropTypes.exact({
    open: PropTypes.arrayOf(CampaignTypeList).isRequired,
    closed: PropTypes.arrayOf(CampaignTypeList).isRequired,
  }).isRequired,
  drives: PropTypes.exact({
    open: PropTypes.arrayOf(CampaignTypeDriveList).isRequired,
    closed: PropTypes.arrayOf(CampaignTypeDriveList).isRequired,
  }).isRequired,
  signCount: PropTypes.number.isRequired,
  user: PropTypes.exact({
    followers: PropTypes.exact({
      count: PropTypes.number.isRequired,
    }).isRequired,
    following: PropTypes.exact({
      count: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};

export default User;
